import React, { useState, useEffect } from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { ContractEvent } from '../../types/contract'; // Import ContractEvent type

interface ContractEventDetailsEditorProps {
  selectedEvent: ContractEvent | null;
  isEditable: boolean;
  handleEventChange: (eventId: string, field: 'name' | 'codeName', value: string) => void; // Only name and codeName
  isEventNameEditing: boolean;
  setIsEventNameEditing: (editing: boolean) => void;
  isEventCodeNameEditing: boolean;
  setIsEventCodeNameEditing: (editing: boolean) => void;
  eventCodeNameError: string | null;
  eventNameInputRef: React.RefObject<HTMLInputElement>;
  eventCodeNameInputRef: React.RefObject<HTMLInputElement>;
}

const ContractEventDetailsEditor: React.FC<ContractEventDetailsEditorProps> = ({
  selectedEvent,
  isEditable,
  handleEventChange,
  isEventNameEditing,
  setIsEventNameEditing,
  isEventCodeNameEditing,
  setIsEventCodeNameEditing,
  eventCodeNameError,
  eventNameInputRef,
  eventCodeNameInputRef,
}) => {
  // Local state for the friendly name input
  const [friendlyName, setFriendlyName] = useState(selectedEvent?.name || '');

  // Update local state when selectedEvent.name changes from props
  useEffect(() => {
    setFriendlyName(selectedEvent?.name || '');
  }, [selectedEvent?.name]);

  if (!selectedEvent) {
    return null;
  }

  const handleFriendlyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFriendlyName(e.target.value);
  };

  const handleFriendlyNameBlur = () => {
    setIsEventNameEditing(false);
    // Only call handleEventChange if the value has actually changed
    if (friendlyName !== selectedEvent.name) {
      handleEventChange(selectedEvent.id, 'name', friendlyName);
    }
  };

  const handleFriendlyNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsEventNameEditing(false);
      setFriendlyName(selectedEvent.name); // Revert to original value
    } else if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger onBlur to save
    }
  };

  return (
    <div className="flex flex-col gap-3 mb-2">
      <div className="flex items-center justify-between mb-4">
        {isEventNameEditing && isEditable ? (
          <input
            ref={eventNameInputRef}
            type="text"
            value={friendlyName} // Use local state
            onChange={handleFriendlyNameChange} // Update local state
            onBlur={handleFriendlyNameBlur} // Commit on blur
            onKeyDown={handleFriendlyNameKeyDown} // Handle key presses
            className="text-gray-900 dark:text-white font-bold text-2xl bg-transparent border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 flex-grow"
            placeholder="Event Friendly Name"
            autoFocus
            disabled={!isEditable}
            tabIndex={1}
          />
        ) : (
          <h2
            className={clsx(
              "flex items-center text-gray-900 dark:text-white font-bold text-2xl px-2 py-1 -mx-2 -my-1 group",
              isEditable ? "cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md" : ""
            )}
            onClick={() => isEditable && setIsEventNameEditing(true)}
          >
            {selectedEvent.name || 'Unnamed Event'}
            {isEditable && (
              <Pencil size={16} className="ml-2 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
          </h2>
        )}
        <div className="flex items-center gap-2 ml-4">
          {isEventCodeNameEditing && isEditable ? (
            <div className="relative">
              <input
                ref={eventCodeNameInputRef}
                type="text"
                value={selectedEvent.codeName || ''} // Ensure value is not null/undefined
                onChange={(e) => handleEventChange(selectedEvent.id, 'codeName', e.target.value)}
                onBlur={() => setIsEventCodeNameEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsEventCodeNameEditing(false);
                    e.currentTarget.value = selectedEvent.codeName || '';
                  } else if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={clsx(
                  `px-2 py-1 rounded-md text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border`,
                  eventCodeNameError ? 'border-red-500' : 'border-transparent',
                  !isEditable && 'opacity-50 cursor-not-allowed'
                )}
                placeholder="event_code_name"
                autoFocus
                disabled={!isEditable}
                tabIndex={2}
              />
              {eventCodeNameError && (
                <div className="absolute top-full left-0 mt-1 text-xs text-red-500 flex items-center">
                  <AlertCircle size={12} className="inline mr-1" />
                  {eventCodeNameError}
                </div>
              )}
            </div>
          ) : (
            <span
              className={clsx(
                "inline-flex items-center px-2 py-1 rounded-md text-sm font-mono font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 transition-colors group",
                isEditable ? "cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800" : "opacity-50 cursor-not-allowed"
              )}
              onClick={() => isEditable && setIsEventCodeNameEditing(true)}
            >
              {selectedEvent.codeName || 'set_code_name'}
              {isEditable && (
                <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
                  <Pencil size={14} className="ml-1 text-blue-600 group-hover:text-blue-800 dark:group-hover:text-blue-200" />
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractEventDetailsEditor;
